import { ReactElement, useState, useEffect } from 'react';

type ChipSelectorProps = {
  options: Array<{ label: string; field: string }>;
  storageKey: string;
  onSelectionChange?: (newState: { [key: string]: boolean }) => void;
  className?: string;
  defaultFields?: string[];
};

export default function ChipSelector(props: ChipSelectorProps): ReactElement {
  const { options, storageKey, onSelectionChange, className = '', defaultFields } = props;

  // Initialize state from localStorage or default to all selected
  const getInitialState = (): { [key: string]: boolean } => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Ensure all options exist in stored state
        const hasAllOptions = options.every((option) => parsed.hasOwnProperty.call(parsed, option.field));
        if (hasAllOptions) {
          return parsed;
        }
      }
    } catch (e) {
      console.error('Error reading state from localStorage:', e);
    }
    // Default to specified fields or all options selected
    if (defaultFields) {
      return options.reduce(
      (acc, option) => ({ ...acc, [option.field]: defaultFields.includes(option.field) }),
      {} as { [key: string]: boolean },
    );
    }
    return options.reduce((acc, option) => ({ ...acc, [option.field]: true }), {} as { [key: string]: boolean });
  };

  const [selectedState, setSelectedState] = useState<{ [key: string]: boolean }>(getInitialState);

  // Save to localStorage whenever selection changes
  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(selectedState));
  }, [selectedState, storageKey]);

  // Notify parent of initial state on mount
  useEffect(() => {
    onSelectionChange?.(selectedState);
  }, []); // Empty dependency array means this runs once on mount

  const chipStyle = 'cr-flex cr-items-center cr-justify-center cr-text-center';
  const activeChipStyle = 'chip-active';
  const inactiveChipStyle = 'chip-inactive';

  const toggleAll = () => {
    const allSelected = Object.values(selectedState).every((value) => value);
    const newState = options.reduce(
      (acc, option) => ({ ...acc, [option.field]: !allSelected }),
      {} as { [key: string]: boolean },
    );
    setSelectedState(newState);
    onSelectionChange?.(newState);
  };

  const toggleOption = (field: string) => {
    const newState = {
      ...selectedState,
      [field]: !selectedState[field],
    };
    setSelectedState(newState);
    onSelectionChange?.(newState);
  };

  return (
    <div className={`cr-grid cr-grid-cols-4 cr-gap-2 cr-pt-4 md:cr-grid-cols-6 lg:cr-grid-cols-8 ${className}`}>
      <div
        className={`${chipStyle} ${
          Object.values(selectedState).every((value) => value) ? activeChipStyle : inactiveChipStyle
        }`}
        onClick={toggleAll}
      >
        All
      </div>
      {options.map((option) => (
        <div
          key={option.field}
          className={`${chipStyle} ${selectedState[option.field] ? activeChipStyle : inactiveChipStyle}`}
          onClick={() => toggleOption(option.field)}
        >
          {option.label}
        </div>
      ))}
    </div>
  );
}
